import React from "react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Download, Copy, ArrowLeft, Share2, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { formatDistanceToNow } from "date-fns";

interface Slide {
  title: string;
  bullets: string[];
  image_prompt?: string;
}

interface PitchDeck {
  id: string;
  idea_id: string;
  title: string;
  audience: string;
  tone: string;
  slide_count: number;
  outline: Slide[];
  status: string;
  created_at: string;
  is_public: boolean;
  public_token: string;
  view_count: number;
  last_viewed_at: string;
}

const DeckViewer = () => {
  const { id } = useParams<{ id: string }>();
  const [deck, setDeck] = useState<PitchDeck | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSharing, setIsSharing] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/signin");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (id && user) {
      fetchDeck();
    }
  }, [id, user]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        prevSlide();
      } else if (e.key === "ArrowRight") {
        nextSlide();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [currentSlide, deck]);

  const fetchDeck = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("pitch_decks")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      setDeck(data as unknown as PitchDeck);
    } catch (err: any) {
      console.error("Error fetching deck:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to load deck",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    window.print();
  };

  const handleCopyMarkdown = () => {
    if (!deck) return;

    const markdown = deck.outline
      .map((slide, idx) => {
        const bullets = slide.bullets.map(b => `- ${b}`).join("\n");
        return `# Slide ${idx + 1}: ${slide.title}\n\n${bullets}\n`;
      })
      .join("\n");

    navigator.clipboard.writeText(markdown);
    toast({
      title: "Copied!",
      description: "Deck outline copied as Markdown",
    });
  };

  const handleTogglePublic = async () => {
    if (!deck) return;

    setIsSharing(true);
    try {
      const newIsPublic = !deck.is_public;

      const { error } = await supabase
        .from("pitch_decks")
        .update({ is_public: newIsPublic })
        .eq("id", deck.id);

      if (error) throw error;

      setDeck({ ...deck, is_public: newIsPublic });

      if (newIsPublic && deck.public_token) {
        const shareUrl = `${window.location.origin}/deck/public/${deck.public_token}`;
        await navigator.clipboard.writeText(shareUrl);
        toast({
          title: "Deck is now public!",
          description: "Share link copied to clipboard",
        });
      } else {
        toast({
          title: "Deck is now private",
          description: "The public link has been disabled",
        });
      }

      // Refetch to get the generated token if first time public
      await fetchDeck();
    } catch (err: any) {
      console.error("Error toggling public:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to update sharing settings",
        variant: "destructive",
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyShareLink = () => {
    if (!deck?.public_token) return;
    const shareUrl = `${window.location.origin}/deck/public/${deck.public_token}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: "Link copied!",
      description: "Share this link with investors",
    });
  };

  const nextSlide = () => {
    if (deck && currentSlide < deck.outline.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!deck) return null;

  const progress = deck.outline.length > 0 ? ((currentSlide + 1) / deck.outline.length) * 100 : 0;
  const slide = deck.outline[currentSlide];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleCopyMarkdown}>
              <Copy className="h-4 w-4 mr-2" />
              Copy Markdown
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>

        <div className="mb-6 print:hidden">
          <h1 className="text-3xl font-bold">{deck.title}</h1>
          <p className="text-muted-foreground mt-2">
            {deck.audience} • {deck.tone} • {deck.slide_count} slides
          </p>
        </div>

        {/* Sharing Controls */}
        <Card className="mb-6 print:hidden">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Share Publicly
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="public-toggle">Enable public sharing</Label>
                <p className="text-sm text-muted-foreground">
                  Anyone with the link can view this deck
                </p>
              </div>
              <Switch
                id="public-toggle"
                checked={deck.is_public}
                onCheckedChange={handleTogglePublic}
                disabled={isSharing}
              />
            </div>

            {deck.is_public && deck.public_token && (
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyShareLink}
                    className="flex-1"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Share Link
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      window.open(`/deck/public/${deck.public_token}`, "_blank")
                    }
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    {deck.view_count || 0} views
                  </span>
                  {deck.last_viewed_at && (
                    <span>
                      Last viewed {formatDistanceToNow(new Date(deck.last_viewed_at), { addSuffix: true })}
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Slide Navigation */}
        <div className="mb-6 print:hidden">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">
              Slide {currentSlide + 1} of {deck.outline.length}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={prevSlide}
                disabled={currentSlide === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={nextSlide}
                disabled={currentSlide === deck.outline.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Current Slide */}
        <Card className="mb-6 min-h-[400px]">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground mb-2">Slide {currentSlide + 1}</p>
                <CardTitle className="text-3xl">{slide.title}</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 list-disc list-inside text-lg">
              {slide.bullets.map((bullet, idx) => (
                <li key={idx} className="text-muted-foreground">
                  {bullet}
                </li>
              ))}
            </ul>
            {slide.image_prompt && (
              <p className="text-xs text-muted-foreground mt-6 italic print:hidden">
                Image suggestion: {slide.image_prompt}
              </p>
            )}
          </CardContent>
        </Card>

        {/* All Slides for Print */}
        <div className="hidden print:block space-y-6">
          {deck.outline.map((slide, idx) => (
            <Card key={idx} className="print:break-inside-avoid print:page-break-after-always">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground mb-2">Slide {idx + 1}</p>
                    <CardTitle className="text-2xl">{slide.title}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 list-disc list-inside">
                  {slide.bullets.map((bullet, bidx) => (
                    <li key={bidx} className="text-muted-foreground">
                      {bullet}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white; }
          .print\\:hidden { display: none !important; }
          .print\\:page-break-after-always { page-break-after: always; }
          .print\\:break-inside-avoid { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default DeckViewer;
